import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../features.css";
import "./custodian.css";
import API from "../../utils/API";
import { Button, EmptyState, SkeletonText, SparkleIcon } from "../../ui";
import { formatDate } from "../../data/format";
import FindingCard from "./FindingCard";
import QuestionCard from "./QuestionCard";
import { GROUP_ORDER, groupForRule } from "./findingGrammar";

/**
 * The Data-Fix queue (/app/datafixes, §B) against the pinned Custodian
 * contracts. The weekly sweep's OPEN custodian_finding Activities (T1) load in
 * full (paginated at the server's 200/page) and window client-side at 50 per
 * E's "no unwindowed lists, ever"; group chips fold the 13 rule classes into
 * E's display groups off the /count endpoint. Staff questions (T3) share the
 * page under a Questions chip. Staff-only + FEATURES.dataFixes (route/nav gated).
 */

const PAGE_LIMIT = 200; // server max
const WINDOW = 50; // client render window (§B2)
const MAX_PAGES = 10; // safety backstop (~2000 findings)
const TERMINAL = ["resolved", "dismissed"];
const UNDO_SECONDS = 10; // delayed-commit undo window (page-lifted, §B4)

function ruleOf(finding) {
  return (finding.meta && finding.meta.rule) || finding.rule;
}

function DataFixesPage() {
  const [findings, setFindings] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [counts, setCounts] = useState({ open: 0, byRule: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [group, setGroup] = useState("All");
  const [shown, setShown] = useState(WINDOW);
  const [resolvedIds, setResolvedIds] = useState(() => new Set());
  const [searchParams] = useSearchParams();
  const qParam = searchParams.get("q"); // deep-link from the bar receipts / digest

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const all = [];
      for (let page = 0; page < MAX_PAGES; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const res = await API.getCustodianQueue({ page, limit: PAGE_LIMIT });
        const batch = Array.isArray(res.data) ? res.data : [];
        all.push(...batch);
        if (batch.length < PAGE_LIMIT) {
          break;
        }
      }
      setFindings(all);
      try {
        const cres = await API.getCustodianQueueCount();
        setCounts(cres.data && cres.data.byRule ? cres.data : { open: all.length, byRule: {} });
      } catch (e) {
        setCounts({ open: all.length, byRule: {} }); // counts are a nicety, not the list
      }
      try {
        const qres = await API.getQuestions();
        setQuestions(Array.isArray(qres.data) ? qres.data : []);
      } catch (e) {
        setQuestions([]);
      }
      setResolvedIds(new Set());
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDone = useCallback((f) => {
    // The card collapses to a receipt in place (§B4); we track it only to keep
    // the chip counts honest.
    setResolvedIds((prev) => {
      const next = new Set(prev);
      next.add(f._id);
      return next;
    });
  }, []);

  const onAsked = useCallback((q) => {
    // L-4: optimistically surface the just-created question so the Questions chip
    // bumps without a reload (the server reconciles on the next load).
    setQuestions((prev) => [q, ...prev]);
  }, []);

  // ── Delayed-commit resolve, LIFTED to the page (post-deploy polish, ruled).
  // The pending state + 10s timer live HERE, not in the FindingCard — so a card
  // unmounting on a chip switch no longer commits the resolve early or loses its
  // receipt. The timer runs regardless of which cards are mounted; the receipt
  // re-renders from this map when a card remounts. Commit-on-PAGE-unmount (real
  // navigation away) still never loses a held resolve.
  // resolutionsRef is the AUTHORITATIVE store (mutated synchronously in the
  // handlers, like the old FindingCard pendingRef) so commit-on-unmount never
  // races a pending render; `resolutions` state just mirrors it for rendering.
  const [resolutions, setResolutions] = useState({}); // { [id]: { label, dismissed, phase, reversal, resolution, note, finding } }
  const resolutionsRef = useRef({});
  const resolveTimers = useRef({});
  const committedRef = useRef({});

  const publishResolutions = useCallback((next) => {
    resolutionsRef.current = next;
    setResolutions(next);
  }, []);

  const commitResolve = useCallback(
    async (id, entry) => {
      if (committedRef.current[id]) {
        return;
      }
      committedRef.current[id] = true;
      try {
        await API.resolveCustodianFinding(
          id,
          entry.note
            ? { resolution: entry.resolution, note: entry.note }
            : { resolution: entry.resolution }
        );
      } catch (e) {
        // 409 ALREADY_RESOLVED or a failure: the data change (if any) stands and
        // the finding clears next sweep. Don't resurrect the card.
      }
      onDone(entry.finding); // count decrements at COMMIT, not apply (E H-1)
    },
    [onDone]
  );

  const beginResolve = useCallback(
    (finding, spec) => {
      const id = finding._id;
      window.clearTimeout(resolveTimers.current[id]);
      if (!spec.resolution) {
        // Display-only receipt (Ask the Fawn): no PATCH, no undo window, but the
        // finding is handled → decrement the count.
        publishResolutions({
          ...resolutionsRef.current,
          [id]: { label: spec.label, dismissed: false, phase: "committed", reversal: null }
        });
        onDone(finding);
        return;
      }
      committedRef.current[id] = false;
      const entry = {
        label: spec.label,
        dismissed: Boolean(spec.dismissed),
        phase: "pending",
        reversal: spec.reversal || null,
        resolution: spec.resolution,
        note: spec.note,
        finding
      };
      publishResolutions({ ...resolutionsRef.current, [id]: entry });
      resolveTimers.current[id] = window.setTimeout(async () => {
        await commitResolve(id, entry);
        const cur = resolutionsRef.current[id];
        if (cur) {
          publishResolutions({ ...resolutionsRef.current, [id]: { ...cur, phase: "committed" } });
        }
      }, UNDO_SECONDS * 1000);
    },
    [commitResolve, onDone, publishResolutions]
  );

  const undoResolve = useCallback(
    (id) => {
      window.clearTimeout(resolveTimers.current[id]);
      const entry = resolutionsRef.current[id];
      const next = { ...resolutionsRef.current };
      delete next[id];
      publishResolutions(next);
      if (entry && entry.reversal) {
        Promise.resolve(entry.reversal()).catch(() => {});
      }
    },
    [publishResolutions]
  );

  // Commit any still-pending resolves when the PAGE unmounts (navigating away).
  useEffect(
    () => () => {
      Object.values(resolveTimers.current).forEach((t) => window.clearTimeout(t));
      Object.entries(resolutionsRef.current).forEach(([id, entry]) => {
        if (entry.phase === "pending" && !committedRef.current[id]) {
          commitResolve(id, entry);
        }
      });
    },
    [commitResolve]
  );

  // Non-terminal questions; a resolved-this-session one stays as a receipt in
  // place (its card owns that), so this doesn't filter by resolvedIds.
  const activeQuestions = useMemo(
    () => questions.filter((q) => !TERMINAL.includes(q.status)),
    [questions]
  );

  // A deep-link (?q=) or a questions-only queue lands on the Questions chip.
  useEffect(() => {
    if (loading) {
      return;
    }
    if (qParam || (findings.length === 0 && activeQuestions.length > 0)) {
      setGroup("Questions");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, qParam]);

  const resolvedByGroup = useMemo(() => {
    const out = {};
    findings.forEach((f) => {
      if (resolvedIds.has(f._id)) {
        const g = groupForRule(ruleOf(f));
        out[g] = (out[g] || 0) + 1;
      }
    });
    return out;
  }, [findings, resolvedIds]);

  const groupCounts = useMemo(() => {
    const base = {};
    Object.entries(counts.byRule || {}).forEach(([rule, n]) => {
      const g = groupForRule(rule);
      base[g] = (base[g] || 0) + n;
    });
    Object.keys(resolvedByGroup).forEach((g) => {
      base[g] = Math.max(0, (base[g] || 0) - resolvedByGroup[g]);
    });
    return base;
  }, [counts, resolvedByGroup]);

  const visibleGroups = GROUP_ORDER.filter((g) => (groupCounts[g] || 0) > 0);
  const total = Math.max(0, (counts.open || findings.length) - resolvedIds.size);
  const hasFindings = findings.length > 0;
  const hasQuestions = activeQuestions.length > 0;

  // Findings stay in the list after resolving (they become receipts in place).
  const filtered = useMemo(
    () => (group === "All" ? findings : findings.filter((f) => groupForRule(ruleOf(f)) === group)),
    [findings, group]
  );

  // Cap disclosure (§B2, E rider-2): each sweep itemizes up to a per-class cap;
  // when a class FOUND more than it itemized, the queue is showing a window of it.
  // Aggregated over the active group's rules from the additive `lastScan` sibling
  // — null until the first scan on the new backend code, so this degrades to
  // nothing (no line, no crash) until then. Uses found>itemized, NOT found>open
  // (opens drain as staff resolve).
  const capDisclosure = useMemo(() => {
    const scan = counts.lastScan;
    if (!scan || !scan.byRule) {
      return null;
    }
    const inScope = (rule) => group === "All" || groupForRule(rule) === group;
    let shown = 0;
    let found = 0;
    Object.entries(scan.byRule).forEach(([rule, e]) => {
      if (e && inScope(rule)) {
        shown += e.itemized || 0;
        found += e.found || 0;
      }
    });
    return found > shown ? { shown, found } : null;
  }, [counts, group]);

  const selectGroup = (g) => {
    setGroup(g);
    setShown(WINDOW);
  };

  return (
    <div className="shell-page">
      <div className="shell-page__titlerow">
        <h1 className="shell-page__title">Data fixes</h1>
      </div>
      <p className="feat-datafix__lead">
        The Custodian checks the book weekly and proposes fixes — nothing is changed until you say
        so.
      </p>

      {loading ? (
        <SkeletonText lines={6} />
      ) : error ? (
        <div className="fk-card">
          <div className="fk-card__body">
            <EmptyState
              title="Couldn't load the queue"
              message="The Custodian queue didn't load."
              action={
                <Button variant="primary" onClick={load}>
                  Try again
                </Button>
              }
            />
          </div>
        </div>
      ) : !hasFindings && !hasQuestions ? (
        <div className="fk-card">
          <div className="fk-card__body">
            <EmptyState
              icon={<SparkleIcon size={28} />}
              title="Nothing needs fixing 🌿"
              message="The Custodian last checked recently. New findings land here after the next sweep."
            />
          </div>
        </div>
      ) : (
        <>
          {hasFindings ? (
            <p className="feat-datafix__meta">
              {total} open {total === 1 ? "finding" : "findings"}
              {findings[0] ? ` · latest sweep ${formatDate(findings[0].created_at)}` : ""}
            </p>
          ) : null}

          <div className="feat-chips">
            {hasFindings ? (
              <button
                type="button"
                className={`feat-chip${group === "All" ? " feat-chip--on" : ""}`}
                onClick={() => selectGroup("All")}
              >
                All <span className="feat-chip__n">{total}</span>
              </button>
            ) : null}
            {visibleGroups.map((g) => (
              <button
                key={g}
                type="button"
                className={`feat-chip${group === g ? " feat-chip--on" : ""}`}
                onClick={() => selectGroup(g)}
              >
                {g} <span className="feat-chip__n">{groupCounts[g]}</span>
              </button>
            ))}
            {hasQuestions ? (
              <button
                type="button"
                className={`feat-chip${group === "Questions" ? " feat-chip--on" : ""}`}
                onClick={() => selectGroup("Questions")}
              >
                Questions <span className="feat-chip__n">{activeQuestions.length}</span>
              </button>
            ) : null}
          </div>

          {group !== "Questions" && capDisclosure ? (
            <p className="feat-datafix__cap">
              {capDisclosure.shown} shown of {capDisclosure.found} found this sweep — the Custodian
              caps each class; the rest carry to the next.
            </p>
          ) : null}

          <div className="feat-datafix__list">
            {group === "Questions"
              ? activeQuestions.map((q) => (
                  <QuestionCard key={q._id} question={q} onDone={onDone} />
                ))
              : filtered
                  .slice(0, shown)
                  .map((f) => (
                    <FindingCard
                      key={f._id}
                      finding={f}
                      resolution={resolutions[f._id]}
                      onBeginResolve={beginResolve}
                      onUndoResolve={undoResolve}
                      onAsked={onAsked}
                    />
                  ))}
            {group !== "Questions" && filtered.length > shown ? (
              <Button variant="secondary" onClick={() => setShown((s) => s + WINDOW)}>
                Show more ({filtered.length - shown})
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

export default DataFixesPage;
